import React from "react"
import {
  Grid,
  makeStyles,
  Typography,
  Divider,
  Paper,
  Switch,
} from "@material-ui/core"
import PlayArrowIcon from "@material-ui/icons/PlayArrow"
import LockIcon from "@material-ui/icons/Lock"
import Button from "@material-ui/core/Button"

const useStyles = makeStyles(theme => ({
  playerContainer: {
    position: "relative",
    paddingTop: "37.25%",
    height: 0,
    overflow: "hidden",
    maxWidth: "100%",
    [theme.breakpoints.down("sm")]: {
      paddingTop: "56.25%",
    },
    backgroundColor: "#000000",
  },
  iframe: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
  },
  unlockContainer: { height: "100%" },
  unlockPaper: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
    width: "70%",
    [theme.breakpoints.down("sm")]: {
      width: "90%",
    },
  },
  playlist: {
    position: "relative",
    [theme.breakpoints.down("sm")]: {
      position: "static",
    },
  },
  playlistContainer: {
    position: "absolute",
    top: "0",
    bottom: "0",
    right: "0",
    left: "0",
    overflow: "auto",
    [theme.breakpoints.down("sm")]: {
      position: "relative",
    },
  },
  playlistHeader: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  freeVideos: {
    paddingTop: theme.spacing(1),
  },
  module: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
    backgroundColor: theme.palette.secondary[400],
  },
  clip: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    "&:hover": {
      backgroundColor: theme.palette.primary[200],
      cursor: "pointer",
    },
  },
  selectedClip: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    backgroundColor: theme.palette.primary[200],
  },
  lock: {
    color: theme.palette.secondary[800],
    fontSize: 15,
  },
}))

export default function CoursePlayer({ syllabus, price, onBuyCourseClick }) {
  const classes = useStyles()
  const numVideos = syllabus.reduce((acc, module) => acc + module.numVideos, 0)
  let [selectedClip, setSelectedClip] = React.useState(syllabus[0]["clips"][0])
  let [freeVideosOnly, setFreeVideosOnly] = React.useState(false)

  const onClipChange = clip => setSelectedClip(clip)
  const onFreeVideoToggle = () => setFreeVideosOnly(!freeVideosOnly)

  const getUpdatedSyllabus = () => {
    if (!freeVideosOnly) return syllabus
    return syllabus.reduce((acc, currentModule) => {
      const freeClips = currentModule.clips.filter(clip => clip.videoId)
      const updatedModule = Object.assign({}, currentModule, {
        clips: freeClips,
      })
      return [...acc, updatedModule];
    }, [])
  }

  return (
    <Grid container>
      <Grid item xs={12} md={8} className={classes.playerContainer}>
        {selectedClip.videoId && (
          <Player classes={classes} videoId={selectedClip.videoId} />
        )}
        {!selectedClip.videoId && (
          <Unlock classes={classes} price={price} onClick={onBuyCourseClick} />
        )}
      </Grid>

      <Grid item xs={12} md={4} className={classes.playlist}>
        <Grid container className={classes.playlistContainer}>
          <Grid item xs={12} style={{ top: 0, position: "sticky" }}>
            <PlaylistHeader
              classes={classes}
              numVideos={numVideos}
              showFreeVideos={freeVideosOnly}
              onFreeVideoToggle={onFreeVideoToggle}
            />
          </Grid>
          <Grid item xs={12}>
            {getUpdatedSyllabus().map((module, key) => (
              <div key={key}>
                <DisplayModule classes={classes} name={module.moduleName} />
                <DisplayClips
                  classes={classes}
                  clips={module.clips}
                  onClipChange={onClipChange}
                  selectedClipId={selectedClip.id}
                />
              </div>
            ))}
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  )
}

const Player = ({ classes, videoId }) => {
  const videoUrl = "https://player.vimeo.com/video/" + videoId
  return (
    <iframe
      title="video"
      src={videoUrl}
      frameBorder="0"
      className={classes.iframe}
    />
  )
}

const Unlock = ({ classes, onClick, price }) => {
  return (
    <div className={classes.iframe}>
      <Grid
        container
        item
        alignItems="center"
        justify="center"
        className={classes.unlockContainer}
      >
        <Paper className={classes.unlockPaper}>
          <Grid container direction={"column"} alignItems="center">
            <Grid item xs={10}>
              <Typography variant={"h5"} gutterBottom>
                Oops! This Lesson is Locked
              </Typography>
            </Grid>
            <Grid item xs={10}>
              <Typography variant={"subtitle1"} gutterBottom>
                Buy this course to get full access
              </Typography>
            </Grid>
            <Grid item xs={10}>
              <Button
                variant="contained"
                size={"large"}
                color={"primary"}
                onClick={onClick}
              >
                {price + " - UNLOCK COURSE"}
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Grid>
    </div>
  )
}

const PlaylistHeader = ({
  classes,
  numVideos,
  showFreeVideos,
  onFreeVideoToggle,
}) => {
  return (
    <Paper variant="outlined" square>
      <Grid
        container
        justify="space-around"
        alignItems="center"
        className={classes.playlistHeader}
      >
        <Grid item xs={8}>
          <Typography variant="h6" color="textPrimary">
            Course Videos
          </Typography>
        </Grid>
        <Grid item xs={3}>
          <Typography variant="body2" color="textSecondary">
            {numVideos + " lessons"}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <FreeVideosToggle
            classes={classes}
            showFreeVideos={showFreeVideos}
            onToggle={onFreeVideoToggle}
          />
        </Grid>
      </Grid>
    </Paper>
  )
}

const FreeVideosToggle = ({ classes, showFreeVideos, onToggle }) => {
  return (
    <Grid
      container
      justify="space-around"
      alignItems="center"
      className={classes.freeVideos}
    >
      <Grid item xs={8}>
        <Typography variant="caption" align="left">
          FREE VIDEOS
        </Typography>
      </Grid>
      <Grid item xs={3}>
        <Switch
          checked={showFreeVideos}
          onChange={onToggle}
          color="primary"
          inputProps={{ "aria-label": "free video switch" }}
        />
      </Grid>
    </Grid>
  )
}

const DisplayModule = ({ classes, name }) => {
  return (
    <Grid container className={classes.module} justify="space-around">
      <Grid item xs={10}>
        <Typography variant="subtitle2" color="textSecondary">
          {name}
        </Typography>
      </Grid>
    </Grid>
  )
}

const DisplayClips = ({ classes, clips, onClipChange, selectedClipId }) => {
  return (
    <>
      {clips.map((clip, key) => (
        <div key={key}>
          <DisplayClip
            name={clip.name}
            duration={clip.duration}
            videoId={clip.videoId}
            classes={classes}
            onChange={() => onClipChange(clip)}
            isSelected={selectedClipId === clip.id}
          />
          <Divider />
        </div>
      ))}
    </>
  )
}

const DisplayClip = ({
  classes,
  name,
  duration,
  videoId,
  onChange,
  isSelected,
}) => {
  return (
    <Grid
      container
      className={isSelected ? classes.selectedClip : classes.clip}
      justify="space-around"
      onClick={onChange}
    >
      <Grid item xs={1}>
        <PlayArrowIcon color="primary" />
      </Grid>
      <Grid item xs={10}>
        <Grid container>
          <Grid item xs={12}>
            <Typography variant="subtitle1">{name}</Typography>
          </Grid>
          <Grid item xs={12}>
            <ClipDurationAndLock
              duration={duration}
              videoId={videoId}
              classes={classes}
            />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  )
}

const ClipDurationAndLock = ({ classes, duration, videoId }) => {
  return (
    <Grid container alignItems="center">
      <Grid item xs={2}>
        <Typography variant="caption">{duration}</Typography>
      </Grid>
      {!videoId && (
        <Grid item xs={2}>
          <LockIcon fontSize={"small"} className={classes.lock} />
        </Grid>
      )}
    </Grid>
  )
}
